import { forwardRef } from 'react';
import type { SvgIcon, SvgIconProps } from '@mui/material';
import MuiAddFileIcon from '@mui/icons-material/NoteAdd';
import MuiAddFolderIcon from '@mui/icons-material/CreateNewFolder';
import MuiAddIcon from '@mui/icons-material/Add';
import MuiAddLocationIcon from '@mui/icons-material/AddLocationAltOutlined';
import MuiAddPersonIcon from '@mui/icons-material/PersonAdd';
import MuiAdminIcon from '@mui/icons-material/AdminPanelSettings';
import MuiAiIcon from '@mui/icons-material/AutoAwesomeOutlined';
import MuiUpIcon from '@mui/icons-material/ArrowDropUp';
import MuiAttachmentIcon from '@mui/icons-material/AttachFile';
import MuiBarChartIcon from '@mui/icons-material/BarChart';
import MuiCameraIcon from '@mui/icons-material/PhotoCamera';
import MuiCancelIcon from '@mui/icons-material/Cancel';
import MuiCaretDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MuiCaretLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import MuiCaretRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MuiCaretUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MuiCategoryIcon from '@mui/icons-material/Category';
import MuiCheckBoxIcon from '@mui/icons-material/CheckBox';
import MuiChecklistIcon from '@mui/icons-material/Checklist';
import MuiCloseIcon from '@mui/icons-material/Close';
import MuiColumnsIcon from '@mui/icons-material/ViewColumnOutlined';
import MuiCompareIcon from '@mui/icons-material/Compare';
import MuiCopyIcon from '@mui/icons-material/ContentCopy';
import MuiCropIcon from '@mui/icons-material/Crop';
import MuiDeleteIcon from '@mui/icons-material/Delete';
import MuiDownIcon from '@mui/icons-material/ArrowDropDown';
import MuiDonutIcon from '@mui/icons-material/DonutLarge';
import MuiDownloadIcon from '@mui/icons-material/FileDownload';
import MuiDragIcon from '@mui/icons-material/DragIndicator';
import MuiEditIcon from '@mui/icons-material/Edit';
import MuiEndCallIcon from '@mui/icons-material/CallEnd';
import MuiErrorIcon from '@mui/icons-material/ReportProblemRounded';
import MuiEventIcon from '@mui/icons-material/Event';
import MuiExposureIcon from '@mui/icons-material/Exposure';
import MuiFileErrorIcon from '@mui/icons-material/SdCardAlertOutlined';
import MuiFileIcon from '@mui/icons-material/InsertDriveFileOutlined';
import MuiFilterIcon from '@mui/icons-material/FilterListRounded';
import MuiFolderIcon from '@mui/icons-material/Folder';
import MuiGridIcon from '@mui/icons-material/GridView';
import MuiHideGridIcon from '@mui/icons-material/GridOffOutlined';
import MuiHideIcon from '@mui/icons-material/VisibilityOff';
import MuiImageIcon from '@mui/icons-material/ImageOutlined';
import MuiInfoIcon from '@mui/icons-material/Info';
import MuiLocationIcon from '@mui/icons-material/LocationOn';
import MuiLogoutIcon from '@mui/icons-material/Logout';
import MuiMenuIcon from '@mui/icons-material/MenuOpenRounded';
import MuiMessagesIcon from '@mui/icons-material/Forum';
import MuiMoreIcon from '@mui/icons-material/MoreHoriz';
import MuiMoveFileIcon from '@mui/icons-material/DriveFileMoveOutlined';
import MuiNotificationIcon from '@mui/icons-material/NotificationsOutlined';
import MuiOptionsIcon from '@mui/icons-material/MoreVert';
import MuiOrderIcon from '@mui/icons-material/LocalShipping';
import MuiPanIcon from '@mui/icons-material/PanTool';
import MuiPauseIcon from '@mui/icons-material/Pause';
import MuiPdfIcon from '@mui/icons-material/PictureAsPdf';
import MuiPeopleIcon from '@mui/icons-material/People';
import MuiPlayIcon from '@mui/icons-material/PlayArrow';
import MuiPolygonIcon from '@mui/icons-material/Polyline';
import MuiQRCodeIcon from '@mui/icons-material/QrCode2';
import MuiRefreshIcon from '@mui/icons-material/Refresh';
import MuiReportsIcon from '@mui/icons-material/Timeline';
import MuiRevenueIcon from '@mui/icons-material/TrendingUp';
import MuiRotationIcon from '@mui/icons-material/ThreeSixtyOutlined';
import MuiScreenshotIcon from '@mui/icons-material/ScreenshotMonitor';
import MuiSearchIcon from '@mui/icons-material/Search';
import MuiSegmentsIcon from '@mui/icons-material/Tune';
import MuiSemicheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import MuiSendIcon from '@mui/icons-material/Send';
import MuiSequenceIcon from '@mui/icons-material/LinearScale';
import MuiSettingsIcon from '@mui/icons-material/Settings';
import MuiShareIcon from '@mui/icons-material/IosShare';
import MuiShowGridIcon from '@mui/icons-material/GridOnOutlined';
import MuiShowIcon from '@mui/icons-material/Visibility';
import MuiSmileIcon from '@mui/icons-material/MoodOutlined';
import MuiSubtractIcon from '@mui/icons-material/Remove';
import MuiSupportIcon from '@mui/icons-material/Support';
import MuiSyncIcon from '@mui/icons-material/Sync';
import MuiTableIcon from '@mui/icons-material/TableRows';
import MuiThreeDimensionsIcon from '@mui/icons-material/ViewInAr';
import MuiTimeIcon from '@mui/icons-material/AccessTime';
import MuiTreatmentsIcon from '@mui/icons-material/AssignmentTurnedIn';
import MuiUncheckBoxIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import MuiUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import MuiVideoIcon from '@mui/icons-material/Movie';
import {
  Archive,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  BookOpenCheckIcon,
  Box,
  Cable,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleDot,
  CircleX,
  Clock,
  CreditCard,
  Form,
  Home,
  Layers,
  Mail,
  Moon,
  Package,
  Printer,
  SquareArrowOutUpRight,
  Sun,
  TextAlignStart,
  TextWrap,
  ToggleLeft,
  TriangleAlert,
  User,
} from 'lucide-react';
import type { BaseIconProps, IconComponent } from './types';

export type { BaseIconProps, IconComponent };

function muiIconComponent(Component: typeof SvgIcon) {
  return forwardRef<SVGSVGElement, SvgIconProps>((props, ref) => (
    <Component ref={ref} {...props} />
  ));
}

function iconComponent(Component: IconComponent) {
  return ({ size = '1rem', ...props }: BaseIconProps) => {
    return <Component size={size} {...props} />;
  };
}

export const AddFileIcon = muiIconComponent(MuiAddFileIcon);
export const AddFolderIcon = muiIconComponent(MuiAddFolderIcon);
export const AddIcon = muiIconComponent(MuiAddIcon);
export const AddLocationIcon = muiIconComponent(MuiAddLocationIcon);
export const AddPersonIcon = muiIconComponent(MuiAddPersonIcon);
export const AdminIcon = muiIconComponent(MuiAdminIcon);
export const AiIcon = muiIconComponent(MuiAiIcon);
export const AlertIcon = iconComponent(CircleAlert);
export const ArchiveIcon = iconComponent(Archive);
export const ArrowDownIcon = iconComponent(ArrowDown);
export const ArrowLeftIcon = iconComponent(ArrowLeft);
export const ArrowRightIcon = iconComponent(ArrowRight);
export const ArrowUpIcon = iconComponent(ArrowUp);
export const AscendingIcon = muiIconComponent(MuiUpIcon);
export const AttachmentIcon = muiIconComponent(MuiAttachmentIcon);
export const AttestationIcon = iconComponent(BookOpenCheckIcon);
export const BarChartIcon = muiIconComponent(MuiBarChartIcon);
export const BooleanIcon = iconComponent(ToggleLeft);
export const CalendarIcon = iconComponent(Calendar);
export const CameraIcon = muiIconComponent(MuiCameraIcon);
export const CancelIcon = muiIconComponent(MuiCancelIcon);
export const CaretDownIcon = muiIconComponent(MuiCaretDownIcon);
export const CaretLeftIcon = muiIconComponent(MuiCaretLeftIcon);
export const CaretRightIcon = muiIconComponent(MuiCaretRightIcon);
export const CaretUpIcon = muiIconComponent(MuiCaretUpIcon);
export const CasesIcon = iconComponent(Archive);
export const CategoryIcon = muiIconComponent(MuiCategoryIcon);
export const CheckBoxIcon = muiIconComponent(MuiCheckBoxIcon);
export const CheckIcon = iconComponent(Check);
export const ChecklistIcon = muiIconComponent(MuiChecklistIcon);
export const CircleXIcon = iconComponent(CircleX);
export const ChevronDownIcon = iconComponent(ChevronDown);
export const ChevronRightIcon = iconComponent(ChevronRight);
export const ClockIcon = iconComponent(Clock);
export const CloseIcon = muiIconComponent(MuiCloseIcon);
export const ColumnsIcon = muiIconComponent(MuiColumnsIcon);
export const CompareIcon = muiIconComponent(MuiCompareIcon);
export const ConnectIcon = iconComponent(Cable);
export const CopyIcon = muiIconComponent(MuiCopyIcon);
export const CreditCardIcon = iconComponent(CreditCard);
export const CropIcon = muiIconComponent(MuiCropIcon);
export const DeleteIcon = muiIconComponent(MuiDeleteIcon);
export const DescendingIcon = muiIconComponent(MuiDownIcon);
export const DoneIcon = iconComponent(CircleCheck);
export const DonutIcon = muiIconComponent(MuiDonutIcon);
export const DownloadIcon = muiIconComponent(MuiDownloadIcon);
export const DragIcon = muiIconComponent(MuiDragIcon);
export const EditIcon = muiIconComponent(MuiEditIcon);
export const EndCallIcon = muiIconComponent(MuiEndCallIcon);
export const ErrorIcon = muiIconComponent(MuiErrorIcon);
export const EventIcon = muiIconComponent(MuiEventIcon);
export const ExposureIcon = muiIconComponent(MuiExposureIcon);
export const FileErrorIcon = muiIconComponent(MuiFileErrorIcon);
export const FileIcon = muiIconComponent(MuiFileIcon);
export const FilterIcon = muiIconComponent(MuiFilterIcon);
export const FolderIcon = muiIconComponent(MuiFolderIcon);
export const FormIcon = iconComponent(Form);
export const GridIcon = muiIconComponent(MuiGridIcon);
export const HideGridIcon = muiIconComponent(MuiHideGridIcon);
export const HideIcon = muiIconComponent(MuiHideIcon);
export const HomeIcon = iconComponent(Home);
export const ImageIcon = muiIconComponent(MuiImageIcon);
export const InfoIcon = muiIconComponent(MuiInfoIcon);
export const LayersIcon = iconComponent(Layers);
export const LocationIcon = muiIconComponent(MuiLocationIcon);
export const LogoutIcon = muiIconComponent(MuiLogoutIcon);
export const LongTextIcon = iconComponent(TextWrap);
export const MailIcon = iconComponent(Mail);
export const MenuIcon = muiIconComponent(MuiMenuIcon);
export const MessagesIcon = muiIconComponent(MuiMessagesIcon);
export const MoonIcon = iconComponent(Moon);
export const MoreIcon = muiIconComponent(MuiMoreIcon);
export const MoveFileIcon = muiIconComponent(MuiMoveFileIcon);
export const NotificationIcon = muiIconComponent(MuiNotificationIcon);
export const OpenExternalIcon = iconComponent(SquareArrowOutUpRight);
export const OptionsIcon = muiIconComponent(MuiOptionsIcon);
export const OrderIcon = muiIconComponent(MuiOrderIcon);
export const PanIcon = muiIconComponent(MuiPanIcon);
export const PauseIcon = muiIconComponent(MuiPauseIcon);
export const PdfIcon = muiIconComponent(MuiPdfIcon);
export const PeopleIcon = muiIconComponent(MuiPeopleIcon);
export const PersonIcon = iconComponent(User);
export const PlayIcon = muiIconComponent(MuiPlayIcon);
export const PolygonIcon = muiIconComponent(MuiPolygonIcon);
export const PrinterIcon = iconComponent(Printer);
export const PrintIcon = iconComponent(Box);
export const ProductIcon = iconComponent(Package);
export const QRCodeIcon = muiIconComponent(MuiQRCodeIcon);
export const RadioInputIcon = iconComponent(CircleDot);
export const RefreshIcon = muiIconComponent(MuiRefreshIcon);
export const ReportsIcon = muiIconComponent(MuiReportsIcon);
export const RevenueIcon = muiIconComponent(MuiRevenueIcon);
export const RotationIcon = muiIconComponent(MuiRotationIcon);
export const ScreenshotIcon = muiIconComponent(MuiScreenshotIcon);
export const SearchIcon = muiIconComponent(MuiSearchIcon);
export const SegmentsIcon = muiIconComponent(MuiSegmentsIcon);
export const SemicheckBoxIcon = muiIconComponent(MuiSemicheckBoxIcon);
export const SendIcon = muiIconComponent(MuiSendIcon);
export const SequenceIcon = muiIconComponent(MuiSequenceIcon);
export const SettingsIcon = muiIconComponent(MuiSettingsIcon);
export const ShareIcon = muiIconComponent(MuiShareIcon);
export const ShortTextIcon = iconComponent(TextAlignStart);
export const ShowGridIcon = muiIconComponent(MuiShowGridIcon);
export const ShowIcon = muiIconComponent(MuiShowIcon);
export const SmileIcon = muiIconComponent(MuiSmileIcon);
export const SubtractIcon = muiIconComponent(MuiSubtractIcon);
export const SunIcon = iconComponent(Sun);
export const SupportIcon = muiIconComponent(MuiSupportIcon);
export const SyncIcon = muiIconComponent(MuiSyncIcon);
export const TableIcon = muiIconComponent(MuiTableIcon);
export const ThreeDimensionsIcon = muiIconComponent(MuiThreeDimensionsIcon);
export const TimeIcon = muiIconComponent(MuiTimeIcon);
export const TreatmentsIcon = muiIconComponent(MuiTreatmentsIcon);
export const UncheckBoxIcon = muiIconComponent(MuiUncheckBoxIcon);
export const UploadIcon = muiIconComponent(MuiUploadIcon);
export const VideoIcon = muiIconComponent(MuiVideoIcon);
export const WarningIcon = iconComponent(TriangleAlert);
